import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import axios, { type AxiosResponse } from 'axios';
import { toast } from 'sonner';

import { Breadcrumb } from '@shared/components/bootstrap/breadcrumb';

import { EditCustomersForm } from '@admin/components/customers/edit_form';
import type { Account } from '@admin/components/customers/edit_form/types';
import { Spinner } from '@admin/components/spinner';
import { accountURL } from '@admin/config/routes';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';

type AccountSummary = {
  id: number;
  number: number;
  customer: { id: number; name: string };
  customers: Account['customers'];
};

const LOAD_ERROR_MESSAGE = 'Unable to load account information. Please try again.';

const useAccountRequest = (accountID: string) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<AccountSummary>> | undefined>();

  useEffect(() => {
    setRequest(axios.get<AccountSummary>(`/accounts/${accountID}.json`));
  }, [accountID]);

  return useAxiosRequest(request);
};

export const EditCustomer: React.FC = () => {
  const { accountID } = useParams<{ accountID: string }>();
  const navigate = useNavigate();
  const { loading, response, error } = useAccountRequest(accountID);
  const account = response?.data;

  useTitle(account ? `Acct: ${account?.customer.name} - Customer - Edit` : undefined);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {LOAD_ERROR_MESSAGE}
      </div>
    );
  }

  if (!account?.customers) {
    return (
      <div className="alert alert-danger" role="alert">
        {LOAD_ERROR_MESSAGE}
      </div>
    );
  }

  const primaryCustomer = account.customer;

  return (
    <>
      <h1 className="page-header text-overflow">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/accounts">Accounts</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to={accountURL({ id: account.id })}>
              #{account.number} ({primaryCustomer.name})
            </NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Customer - Edit</Breadcrumb.Item>
        </Breadcrumb>
      </h1>

      <EditCustomersForm
        account={account}
        onSuccess={() => {
          navigate(`/accounts/${account.id}`);
        }}
        onError={(error) => {
          const message = error.error?.data?.message;
          if (message) toast.error(message);
        }}
      />
    </>
  );
};
